import React, { useState, useRef, useEffect, useMemo, useId } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  icon?: string;
  badge?: string;
  description?: string;
  disabled?: boolean;
}

export type SelectOptionInput = string | SelectOption;

export interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOptionInput[];
  placeholder?: string;
  searchPlaceholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  align?: 'left' | 'right';
  className?: string;
  menuClassName?: string;
  label?: string;
  id?: string;
  name?: string;
  clearable?: boolean;
  emptyText?: string;
}

/**
 * CustomSelect - Modern, searchable, and scrollable dropdown component.
 *
 * @example
 * <CustomSelect
 *   value={platform}
 *   onChange={setPlatform}
 *   options={['All platforms', 'BloomCommerce', 'NovaMarket']}
 *   searchable
 * />
 */
export default function CustomSelect({
  value,
  onChange,
  options,
  placeholder = 'Select an option...',
  searchPlaceholder = 'Search...',
  searchable,
  disabled = false,
  size = 'md',
  align = 'left',
  className = '',
  menuClassName = '',
  label,
  id,
  name,
  clearable = false,
  emptyText = 'No options found',
}: CustomSelectProps) {
  const generatedId = useId();
  const selectId = id || generatedId;

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Normalize options to SelectOption objects
  const normalizedOptions: SelectOption[] = useMemo(() => {
    return options.map((opt) => (typeof opt === 'string' ? { value: opt, label: opt } : opt));
  }, [options]);

  // Determine if search should be enabled (auto-enabled if > 5 options unless explicitly passed)
  const isSearchable = searchable !== undefined ? searchable : normalizedOptions.length > 5;

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return normalizedOptions;
    const lower = search.toLowerCase();
    return normalizedOptions.filter(
      (opt) => opt.label.toLowerCase().includes(lower) || opt.value.toLowerCase().includes(lower)
    );
  }, [normalizedOptions, search]);

  const selectedOption = useMemo(() => {
    return normalizedOptions.find((opt) => opt.value === value);
  }, [normalizedOptions, value]);

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setHighlightedIndex(-1);
      if (isSearchable) {
        // Small timeout to allow render
        const timer = setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, isSearchable]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      const target = items[highlightedIndex] as HTMLElement | undefined;
      target?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          const selected = filteredOptions[highlightedIndex];
          if (!selected.disabled) {
            onChange(selected.value);
            setIsOpen(false);
          }
        }
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Size variations
  const sizeClasses = {
    sm: 'h-9 text-xs px-3 gap-1.5 rounded-lg',
    md: 'h-10 text-sm px-3.5 gap-2 rounded-xl',
    lg: 'h-12 text-sm px-4 gap-2.5 rounded-xl',
  }[size];

  return (
    <div
      ref={containerRef}
      className={`relative inline-block text-left select-none ${className}`}
      onKeyDown={handleKeyDown}
    >
      {name && <input type="hidden" name={name} value={value} />}

      {label && (
        <label htmlFor={selectId} className="block text-xs font-semibold text-foreground-700 mb-1.5">
          {label}
        </label>
      )}

      {/* Select Trigger Button */}
      <button
        id={selectId}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={`w-full flex items-center justify-between border transition-all cursor-pointer ${sizeClasses} ${
          isOpen
            ? 'bg-background-50 border-primary-500 ring-2 ring-primary-500/15 shadow-sm'
            : 'bg-background-50 hover:bg-background-100/60 border-background-200/90 shadow-2xs hover:border-background-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed bg-background-100' : 'text-foreground-900'}`}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedOption?.icon && (
            <i className={`${selectedOption.icon} text-primary-500 text-sm shrink-0`}></i>
          )}
          <span
            className={`truncate font-medium ${
              selectedOption ? 'text-foreground-900' : 'text-foreground-400'
            }`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          {selectedOption?.badge && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md bg-primary-100 text-primary-700">
              {selectedOption.badge}
            </span>
          )}
        </span>

        <span className="flex items-center gap-1 text-foreground-400 shrink-0 ml-2">
          {clearable && value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              className="p-0.5 hover:text-foreground-700 rounded-full transition-colors cursor-pointer"
              title="Clear selection"
            >
              <i className="ri-close-line text-sm"></i>
            </span>
          )}
          <i
            className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-primary-600' : 'text-foreground-400'
            }`}
          ></i>
        </span>
      </button>

      {/* Floating Dropdown Panel */}
      {isOpen && (
        <div
          role="listbox"
          className={`absolute ${
            align === 'right' ? 'right-0' : 'left-0'
          } top-full mt-1.5 min-w-[200px] w-max max-w-xs z-50 bg-background-50 border border-background-200/90 rounded-2xl shadow-xl shadow-foreground-950/10 overflow-hidden animate-fade-in ${menuClassName}`}
        >
          {/* Search Bar */}
          {isSearchable && (
            <div className="p-2 border-b border-background-200/80 bg-background-100/50">
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-background-50 border border-background-200/90 text-xs shadow-2xs focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500/20">
                <i className="ri-search-line text-foreground-400 text-xs"></i>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setHighlightedIndex(0);
                  }}
                  placeholder={searchPlaceholder}
                  className="w-full bg-transparent text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearch('');
                      searchInputRef.current?.focus();
                    }}
                    className="text-foreground-400 hover:text-foreground-700 p-0.5 cursor-pointer"
                  >
                    <i className="ri-close-line text-xs"></i>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Options List with Custom Scrollbar */}
          <div ref={listRef} className="max-h-56 overflow-y-auto custom-scrollbar p-1.5 space-y-0.5">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-foreground-400 flex flex-col items-center gap-1.5">
                <i className="ri-inbox-line text-base text-foreground-300"></i>
                <span>{emptyText}</span>
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = option.value === value;
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      if (option.disabled) return;
                      onChange(option.value);
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-medium cursor-pointer transition-all ${
                      option.disabled
                        ? 'opacity-40 cursor-not-allowed text-foreground-400'
                        : isSelected
                        ? 'bg-primary-50 text-primary-700 font-semibold'
                        : isHighlighted
                        ? 'bg-background-100 text-foreground-950'
                        : 'text-foreground-700 hover:bg-background-100/70 hover:text-foreground-900'
                    }`}
                  >
                    <div className="flex items-center gap-2 truncate min-w-0">
                      {option.icon && (
                        <i
                          className={`${option.icon} text-sm ${
                            isSelected ? 'text-primary-600' : 'text-foreground-400'
                          }`}
                        ></i>
                      )}
                      <div className="truncate">
                        <p className="truncate leading-normal">{option.label}</p>
                        {option.description && (
                          <p className="text-[10px] text-foreground-400 truncate mt-0.5 font-normal">
                            {option.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {option.badge && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                            isSelected
                              ? 'bg-primary-100 text-primary-800'
                              : 'bg-background-200 text-foreground-600'
                          }`}
                        >
                          {option.badge}
                        </span>
                      )}
                      {isSelected && (
                        <i className="ri-check-line text-primary-600 text-sm font-bold"></i>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

